import { useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 900ms so the copilot fires after the caller finishes a thought, not mid-typing.
const DEBOUNCE_MS = 900;

/**
 * Debounced copilot caller. POSTs to /triage/calls/:id/assist.
 * Dispatches SET_COPILOT_LOADING / SET_COPILOT / SET_COPILOT_ERROR.
 *
 * Anchored to the active question: sends the current question text, the note
 * typed against it, and the next scripted question so the copilot stays in flow.
 *
 * triggerCopilot({ immediate }) — pass immediate:true for objection clicks
 * (fire now, no debounce); otherwise debounced for note typing.
 *
 * flushSession — the SAME PATCH the autosave uses (from useTriageSession). We
 * await it before every /assist call so the backend reads exactly what's typed
 * on screen, not a stale (up-to-10s-old) autosave snapshot. The copilot reads
 * answered-ness + the call-so-far transcript straight from the DB, so without
 * this flush it would treat already-answered questions as unanswered.
 */
export function useCopilot({ callId, currentStepId, currentLineIdx, currentQuestion, currentNote, nextQuestion, clickedObjectionIds, flushSession, dispatch }) {
  const timerRef = useRef(null);
  const argsRef = useRef({});
  argsRef.current = { callId, currentStepId, currentLineIdx, currentQuestion, currentNote, nextQuestion, clickedObjectionIds };
  // Keep the latest flush fn in a ref so the stable `fire` callback always
  // calls the current one without needing it in the dependency array.
  const flushRef = useRef(flushSession);
  flushRef.current = flushSession;
  // Guard against re-firing the copilot for an unchanged (question, note) pair.
  const lastFiredRef = useRef(null);

  const fire = useCallback(async (opts = {}) => {
    const {
      callId: cid,
      currentStepId: stepId,
      currentLineIdx: lineIdx,
      currentQuestion: curQ,
      currentNote: curNote,
      nextQuestion: nextQ,
      clickedObjectionIds: objs,
    } = argsRef.current;
    if (!cid || !stepId) return;
    // Skip if the focused line / its note hasn't changed since the last call.
    // Objection clicks force a fire (immediate) and bypass this guard.
    const sig = `${stepId}|${lineIdx}|${curQ}|${curNote}`;
    if (!opts.force && lastFiredRef.current === sig) return;
    lastFiredRef.current = sig;
    dispatch({ type: 'SET_COPILOT_LOADING' });
    // Persist current in-memory notes/step/objections to the DB BEFORE the LLM
    // call so the handler (which reads session.question_notes from the DB) sees
    // the live answers. Soft: a flush failure must not block the suggestion.
    try {
      if (flushRef.current) await flushRef.current();
    } catch (e) {
      console.error('Copilot pre-assist flush failed:', e);
    }
    try {
      const res = await axios.post(`${API}/triage/calls/${cid}/assist`, {
        call_id: cid,
        current_step_id: stepId,
        // Index of the focused line within the current step's spoken_lines.
        // Authoritative active line — reflects FOCUS, not just answered-ness, so
        // the copilot helps ask a still-empty focused question rather than skipping it.
        current_line_index: (typeof lineIdx === 'number' ? lineIdx : null),
        current_question: curQ || '',
        current_note: (curNote || '').slice(-400),
        next_question: nextQ || '',
        clicked_objection_ids: objs || [],
      });
      if (res.data?.error || res.data?.suggestion == null) {
        dispatch({ type: 'SET_COPILOT_ERROR', error: res.data?.error || 'copilot_unavailable' });
      } else {
        dispatch({ type: 'SET_COPILOT', suggestion: res.data.suggestion });
      }
    } catch (err) {
      console.error('Copilot assist failed:', err);
      dispatch({ type: 'SET_COPILOT_ERROR', error: 'copilot_unavailable' });
    }
  }, [dispatch]);

  const triggerCopilot = useCallback((opts = {}) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (opts.immediate) {
      // Objection clicks always fire, even if (question, note) is unchanged.
      fire({ force: true });
    } else {
      timerRef.current = setTimeout(() => fire(), DEBOUNCE_MS);
    }
  }, [fire]);

  return { triggerCopilot };
}
